import { DeleteOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons'
import { Alert, Button, Card, Form, Input, Modal, Popconfirm, Select, Space, Switch, Table, Typography } from 'antd'
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  useAvailableReportPlatformTypes,
  useDeleteNotificationOptionMutation,
  useNotificationOptionsList,
  useSaveNotificationOptionsListMutation,
} from '../../hooks/useReports'
import { useAuthSession } from '../../lib/session'
import type { NotificationOption } from '../../types/reports'
import { useToast } from '../../contexts/ToastContext'
import { getPlatformTypeLabel, PLATFORM_TYPE_OPTIONS, REPORT_SUPPORT_DOCUMENTATION_URL } from './reportOptionConstants'
import { entityNameSchema, longTextSchema, shortTextSchema, zodAntdValidator } from '../../lib/validation'

const zeroGuid = '00000000-0000-0000-0000-000000000000'
const optionNameSchema = entityNameSchema('Name')
const optionTitleSchema = entityNameSchema('Title')
const parameterSchema = shortTextSchema('Parameter')
const optionDescriptionSchema = longTextSchema('Description')

type ParameterRow = {
  parameter: string
}

type OptionFormValues = {
  notificationOptionID: string
  name: string
  title: string
  defaultDAXQuery: string
  description?: string
  platformType: number
  parameters: ParameterRow[]
  filteredToRecipients: boolean
}

export default function ManageNotificationOptionsPage() {
  const session = useAuthSession()
  const navigate = useNavigate()
  const clientId = session?.currentClientId || ''
  const { addToast } = useToast()

  const [form] = Form.useForm<OptionFormValues>()
  const [searchText, setSearchText] = useState('')
  const [modalOpen, setModalOpen] = useState(false)
  const [editingOption, setEditingOption] = useState<NotificationOption | null>(null)
  const [pageSize, setPageSize] = useState(10)
  const [currentPage, setCurrentPage] = useState(1)

  const optionsQuery = useNotificationOptionsList(clientId)
  const platformTypesQuery = useAvailableReportPlatformTypes(clientId)
  const saveMutation = useSaveNotificationOptionsListMutation()
  const deleteMutation = useDeleteNotificationOptionMutation()

  const filteredOptions = useMemo(() => {
    const value = searchText.trim().toLowerCase()
    if (!value) return optionsQuery.data || []
    return (optionsQuery.data || []).filter((item) => item.name.toLowerCase().includes(value))
  }, [optionsQuery.data, searchText])

  useEffect(() => {
    setCurrentPage(1)
  }, [searchText])

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(filteredOptions.length / pageSize))
    if (currentPage > totalPages) {
      setCurrentPage(totalPages)
    }
  }, [currentPage, filteredOptions.length, pageSize])

  const platformOptions =
    platformTypesQuery.data?.map((item) => ({ value: item.value, label: item.name })) || PLATFORM_TYPE_OPTIONS

  const openCreateModal = () => {
    setEditingOption(null)
    form.setFieldsValue({
      notificationOptionID: zeroGuid,
      name: '',
      title: '',
      defaultDAXQuery: '',
      description: '',
      platformType: 2,
      parameters: [],
      filteredToRecipients: false,
    })
    setModalOpen(true)
  }

  const openEditModal = useCallback((option: NotificationOption) => {
    setEditingOption(option)
    form.setFieldsValue({
      notificationOptionID: option.notificationOptionID,
      name: option.name,
      title: option.title,
      defaultDAXQuery: option.defaultDAXQuery,
      description: option.description || '',
      platformType: option.platformType,
      parameters: String(option.parameters || '')
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean)
        .map((parameter) => ({ parameter })),
      filteredToRecipients: Boolean(option.filteredToRecipients),
    })
    setModalOpen(true)
  }, [form])

  const closeModal = () => {
    setModalOpen(false)
    setEditingOption(null)
    form.resetFields()
  }

  const saveOption = async () => {
    try {
      const values = await form.validateFields()
      const payload: NotificationOption = {
        notificationOptionID: values.notificationOptionID || zeroGuid,
        name: values.name,
        title: values.title,
        defaultDAXQuery: values.defaultDAXQuery,
        defaultEmailTemplate: '',
        description: values.description || '',
        platformType: Number(values.platformType),
        parameters: (values.parameters || [])
          .map((item) => item.parameter.trim())
          .filter(Boolean)
          .join(','),
        filteredToRecipients: Boolean(values.filteredToRecipients),
      }

      const ok = await saveMutation.mutateAsync({ notificationOptionsList: payload })
      if (!ok) {
        addToast('Failed to save notification option.', 'error')
        return
      }

      addToast('Notification option saved successfully.', 'success')
      closeModal()
      await optionsQuery.refetch()
    } catch (error) {
      if (error instanceof Error) {
        addToast(error.message, 'error')
      }
    }
  }

  const deleteOption = async () => {
    if (!editingOption) return
    const ok = await deleteMutation.mutateAsync(editingOption.notificationOptionID)
    if (!ok) {
      addToast('Failed to delete notification option.', 'error')
      return
    }

    addToast('Notification option deleted.', 'success')
    closeModal()
    await optionsQuery.refetch()
  }

  const columns = useMemo<ColumnsType<NotificationOption>>(
    () => [
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        render: (_, row) => (
          <Button type="link" className="px-0" onClick={() => openEditModal(row)}>
            {row.name}
          </Button>
        ),
      },
      {
        title: 'Title',
        dataIndex: 'title',
        key: 'title',
      },
      {
        title: 'Applicable Platform',
        dataIndex: 'platformType',
        key: 'platformType',
        render: (value: number) => getPlatformTypeLabel(value),
      },
      {
        title: 'Created By',
        dataIndex: 'createdBy',
        key: 'createdBy',
        render: (value: string | undefined) => value || '-',
      },
    ],
    [openEditModal]
  )

  const pagination: TablePaginationConfig = {
    current: currentPage,
    pageSize,
    showSizeChanger: true,
    pageSizeOptions: ['10', '20', '50', '100'],
    onChange: (page, size) => {
      setCurrentPage(page)
      if (size && size !== pageSize) {
        setPageSize(size)
      }
    },
  }

  return (
    <Space direction="vertical" size={16} className="w-full">
      <Card>
        <Space className="w-full justify-between" align="start">
          <div>
            <Typography.Title level={3} className="!mb-1">
              Manage Report Option
            </Typography.Title>
            <Typography.Text type="secondary">Current client: {session?.currentClientName || 'Unknown'}</Typography.Text>
          </div>
          <Space wrap>
            <Button onClick={() => navigate('/reports/created')}>Back</Button>
            <Button onClick={() => navigate('/reports/options')}>Report Builder Options</Button>
            <Button onClick={() => window.open(REPORT_SUPPORT_DOCUMENTATION_URL, '_blank', 'noopener,noreferrer')}>
              View Support Documentation
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
              Add Report Option
            </Button>
          </Space>
        </Space>
      </Card>

      {optionsQuery.isError ? (
        <Alert
          type="error"
          showIcon
          message="Failed to load notification options"
          description={optionsQuery.error instanceof Error ? optionsQuery.error.message : 'Unknown error'}
          action={<Button onClick={() => optionsQuery.refetch()}>Retry</Button>}
        />
      ) : null}

      <Card>
        <Space direction="vertical" size={16} className="w-full">
          <Input
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            placeholder="Search by option name"
            suffix={<SearchOutlined />}
            allowClear
            style={{ maxWidth: 320 }}
          />
          <Table<NotificationOption>
            rowKey="notificationOptionID"
            loading={optionsQuery.isLoading}
            columns={columns}
            dataSource={filteredOptions}
            pagination={pagination}
          />
        </Space>
      </Card>

      <Modal
        title={editingOption ? 'Edit Report Option' : 'Add Report Option'}
        open={modalOpen}
        onCancel={closeModal}
        onOk={() => void saveOption()}
        width={760}
        okText="Save"
        okButtonProps={{ loading: saveMutation.isPending }}
        footer={(_, { OkBtn, CancelBtn }) => (
          <Space className="w-full justify-between">
            <Space>
              {editingOption ? (
                <>
                  <Popconfirm
                    title="Delete report option"
                    description="Are you sure you want to delete this report option?"
                    okButtonProps={{ danger: true }}
                    onConfirm={() => void deleteOption()}
                  >
                    <Button danger loading={deleteMutation.isPending}>
                      Delete
                    </Button>
                  </Popconfirm>
                  <Button onClick={() => navigate(`/reports/view/${editingOption.notificationOptionID}`)}>View Schedules</Button>
                </>
              ) : null}
            </Space>
            <Space>
              <CancelBtn />
              <OkBtn />
            </Space>
          </Space>
        )}
      >
        <Form<OptionFormValues> form={form} layout="vertical">
          <Form.Item name="notificationOptionID" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="name" label="Name" rules={[{ validator: zodAntdValidator(optionNameSchema) }]}>
            <Input />
          </Form.Item>
          <Form.Item name="title" label="Title" rules={[{ validator: zodAntdValidator(optionTitleSchema) }]}>
            <Input />
          </Form.Item>
          <Form.List name="parameters">
            {(fields, { add, remove }) => (
              <Form.Item label="Parameters">
                <Space direction="vertical" size={8} className="w-full">
                  <Button onClick={() => add({ parameter: '' })}>Add New Parameter</Button>
                  {fields.map((field) => (
                    <Space key={field.key} align="start" className="w-full">
                      <Form.Item
                        {...field}
                        name={[field.name, 'parameter']}
                        className="mb-0 flex-1"
                        rules={[{ validator: zodAntdValidator(parameterSchema) }]}
                      >
                        <Input placeholder="Parameter" />
                      </Form.Item>
                      <Button danger icon={<DeleteOutlined />} onClick={() => remove(field.name)}>
                        Remove
                      </Button>
                    </Space>
                  ))}
                </Space>
              </Form.Item>
            )}
          </Form.List>
          <Form.Item
            name="defaultDAXQuery"
            label="Default DAX Query"
            rules={[{ required: true, message: 'Default DAX Query is required' }]}
          >
            <Input.TextArea rows={4} />
          </Form.Item>
          <Form.Item name="platformType" label="Applicable Platform" rules={[{ required: true, message: 'Platform is required' }]}>
            <Select options={platformOptions} />
          </Form.Item>
          <Form.Item name="description" label="Description" rules={[{ validator: zodAntdValidator(optionDescriptionSchema) }]}>
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="filteredToRecipients" label="Filtered To Recipients" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  )
}
